import React, { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Inbox, Lightbulb, Loader2, Plus, X } from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import StoreShell from "../components/StoreShell";
import { useStoreCustody } from "../utils/useStoreCustody";
import { fmtDate, fmtQty } from "../utils/stores";
import {
  useStoreGetStoresQuery,
  useStoreGetItemsQuery,
  useStoreGetRequestsQuery,
  useStoreCreateRequestMutation,
  useStoreApproveRequestMutation,
  useStoreRejectRequestMutation,
  useStoreCancelRequestMutation,
  useStoreGetSuggestionsQuery,
  useStoreCreateSuggestionMutation,
  useStoreAcceptSuggestionMutation,
  useStoreRejectSuggestionMutation,
} from "../api/storesApi";

type UiMsg = { type: "success" | "error"; text: string } | null;

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
  cancelled: "bg-gray-50 text-gray-500 border-gray-200",
};

function Status({ value }: { value: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full ${STATUS_STYLE[value] ?? STATUS_STYLE.cancelled}`}>
      {value}
    </span>
  );
}

/**
 * What a branch store asks for.
 *
 * Custody means a custodian moves only their own stock and stock reaches a
 * branch only from the main store, so a branch that has run out cannot act —
 * it asks. This screen is both sides of that: what my stores have asked for,
 * and what has been asked of the store I keep.
 */
function RequestsInner() {
  const { canWork } = useStoreCustody();

  const { data: stores = [] } = useStoreGetStoresQuery();
  const { data: itemsData } = useStoreGetItemsQuery({ status: "active" });
  const { data: requests = [], isLoading } = useStoreGetRequestsQuery();
  const { data: suggestions = [] } = useStoreGetSuggestionsQuery();

  const [createRequest, { isLoading: asking }] = useStoreCreateRequestMutation();
  const [approve, { isLoading: approving }] = useStoreApproveRequestMutation();
  const [reject, { isLoading: rejecting }] = useStoreRejectRequestMutation();
  const [cancel] = useStoreCancelRequestMutation();
  const [suggest, { isLoading: suggesting }] = useStoreCreateSuggestionMutation();
  const [accept] = useStoreAcceptSuggestionMutation();
  const [rejectSuggestion] = useStoreRejectSuggestionMutation();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [asking_, setAsking] = useState(false);
  const [suggesting_, setSuggesting] = useState(false);

  const items = useMemo(() => itemsData?.rows ?? [], [itemsData]);
  const myStores = useMemo(() => stores.filter((s: any) => canWork(s) && !s.is_main), [stores, canWork]);
  const mainStore = useMemo(() => stores.find((s: any) => s.is_main), [stores]);

  const [reqForm, setReqForm] = useState({ store_id: "", store_item_id: "", quantity: "", note: "" });
  const [sugForm, setSugForm] = useState({ store_id: "", name: "", category: "", unit: "pcs", suggested_price: "", reason: "" });

  const flash = (m: UiMsg) => {
    setMsg(m);
    if (m) setTimeout(() => setMsg(null), 7000);
  };

  /** Asked OF a store I keep — the ones I answer. */
  const inbox = useMemo(
    () => (requests as any[]).filter((r) => r.status === "pending" && canWork(r.from_store ?? r.fromStore)),
    [requests, canWork],
  );

  /** Raised BY my stores — the ones I am waiting on. */
  const outbox = useMemo(
    () => (requests as any[]).filter((r) => !inbox.includes(r)),
    [requests, inbox],
  );

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res: any = await createRequest({
        store_id: reqForm.store_id,
        store_item_id: reqForm.store_item_id,
        quantity: Number(reqForm.quantity),
        note: reqForm.note.trim() || undefined,
      }).unwrap();
      setAsking(false);
      setReqForm({ store_id: "", store_item_id: "", quantity: "", note: "" });
      flash({ type: "success", text: res?.message || "Request sent." });
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not send that request.") });
    }
  };

  const submitSuggestion = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res: any = await suggest({
        store_id: sugForm.store_id || undefined,
        name: sugForm.name.trim(),
        category: sugForm.category.trim() || undefined,
        unit: sugForm.unit.trim() || undefined,
        suggested_price: sugForm.suggested_price ? Number(sugForm.suggested_price) : undefined,
        reason: sugForm.reason.trim() || undefined,
      }).unwrap();
      setSuggesting(false);
      setSugForm({ store_id: "", name: "", category: "", unit: "pcs", suggested_price: "", reason: "" });
      flash({ type: "success", text: res?.message || "Suggested." });
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not send that suggestion.") });
    }
  };

  const onApprove = async (r: any) => {
    const asked = Number(r.quantity);
    const typed = window.prompt(
      `How much of ${r.item?.name} are you sending? Asked for ${fmtQty(asked)} ${r.item?.unit}.`,
      String(asked),
    );
    if (typed === null) return;

    try {
      const res: any = await approve({ id: r.id, quantity: Number(typed) }).unwrap();
      flash({ type: "success", text: res?.message || "Approved." });
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not approve that request.") });
    }
  };

  return (
    <StoreShell
      title="Requests"
      subtitle="What your stores have asked the main store for, and what you have been asked to send."
      actions={
        <AccessGate perm={PERMS.store.movement}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSuggesting(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Lightbulb className="w-4 h-4" /> Suggest an item
            </button>
            <button
              onClick={() => setAsking(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg"
            >
              <Plus className="w-4 h-4" /> Request stock
            </button>
          </div>
        </AccessGate>
      }
    >
      {msg && (
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 text-sm font-medium ${
            msg.type === "success"
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span className="leading-relaxed">{msg.text}</span>
        </div>
      )}

      {/* Asked of me */}
      {inbox.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Inbox className="w-4 h-4 text-amber-600" />
            <h2 className="font-bold text-gray-800">Waiting on you</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Approving sends the stock in the same act — there is no separate transfer to remember.
          </p>

          <div className="space-y-2">
            {inbox.map((r: any) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {r.store?.name} wants {fmtQty(r.quantity)} {r.item?.unit} of {r.item?.name}
                  </div>
                  <div className="text-xs text-gray-400">
                    Asked {fmtDate(r.created_at)}
                    {r.requested_by?.name ? ` by ${r.requested_by.name}` : ""}
                    {r.note ? ` · ${r.note}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => onApprove(r)}
                    disabled={approving}
                    className="px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-xs font-medium disabled:opacity-40"
                  >
                    Approve and send
                  </button>
                  <button
                    onClick={async () => {
                      const why = window.prompt("Why is it being turned down? (optional)") ?? undefined;
                      try {
                        await reject({ id: r.id, decision_note: why }).unwrap();
                        flash({ type: "success", text: "Request turned down." });
                      } catch (err) {
                        flash({ type: "error", text: describeApiError(err, "Could not update that request.") });
                      }
                    }}
                    disabled={rejecting}
                    className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Turn down
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My requests */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h2 className="font-bold text-gray-800">Stock requests</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Nothing moves until the store being asked approves it.
          </p>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400 animate-pulse">Loading…</div>
        ) : outbox.length === 0 ? (
          <div className="py-12 text-center px-6">
            <Inbox className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No requests yet</p>
            <p className="text-xs text-gray-400 mt-1">
              {mainStore
                ? `Ask ${mainStore.name} for stock your store has run out of.`
                : "No main store is set, so there is nowhere to request from yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                  <th className="px-4 py-2 font-medium">Asked</th>
                  <th className="px-4 py-2 font-medium">Item</th>
                  <th className="px-4 py-2 font-medium">For</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {outbox.map((r: any) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item?.name}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.item?.code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.store?.name}</td>
                    <td className="px-4 py-3 text-right text-gray-800">
                      {fmtQty(r.quantity)}
                      {r.approved_quantity != null && Number(r.approved_quantity) !== Number(r.quantity) && (
                        <div className="text-[11px] text-emerald-600">sent {fmtQty(r.approved_quantity)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Status value={r.status} />
                      {r.decision_note && (
                        <div className="text-[11px] text-gray-400 mt-0.5">{r.decision_note}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" && canWork(r.store) && (
                        <button
                          onClick={async () => {
                            try {
                              await cancel(r.id).unwrap();
                              flash({ type: "success", text: "Request withdrawn." });
                            } catch (err) {
                              flash({ type: "error", text: describeApiError(err, "Could not withdraw it.") });
                            }
                          }}
                          className="text-xs text-gray-400 hover:text-gray-700"
                        >
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Suggestions */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h2 className="font-bold text-gray-800">Item suggestions</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Items the register does not carry yet. Accepting one adds it to the register.
          </p>
        </div>

        {(suggestions as any[]).length === 0 ? (
          <div className="py-12 text-center px-6">
            <Lightbulb className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nothing suggested yet</p>
            <p className="text-xs text-gray-400 mt-1">
              If your store needs something the register does not carry, suggest it.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {(suggestions as any[]).map((s: any) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {s.name}
                    {s.unit ? <span className="text-gray-400 font-normal"> · per {s.unit}</span> : null}
                  </div>
                  <div className="text-xs text-gray-400">
                    {s.store?.name ? `${s.store.name} · ` : ""}
                    {fmtDate(s.created_at)}
                    {s.reason ? ` · ${s.reason}` : ""}
                  </div>
                  {s.item?.code && (
                    <div className="text-[11px] text-emerald-600 mt-0.5">Added as {s.item.code}</div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Status value={s.status} />

                  {/* Accepting creates the item, so it sits with whoever may
                      add one — store.manage, or a super admin. */}
                  {s.status === "pending" && (
                    <AccessGate perm={PERMS.store.manage}>
                      <>
                        <button
                          onClick={async () => {
                            const code = window.prompt(`Give "${s.name}" a code for the register:`);
                            if (!code) return;
                            try {
                              const res: any = await accept({ id: s.id, code: code.trim() }).unwrap();
                              flash({ type: "success", text: res?.message || "Added to the register." });
                            } catch (err) {
                              flash({ type: "error", text: describeApiError(err, "Could not accept it.") });
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-xs font-medium"
                        >
                          Accept
                        </button>
                        <button
                          onClick={async () => {
                            const why = window.prompt("Why not? (optional)") ?? undefined;
                            try {
                              await rejectSuggestion({ id: s.id, decision_note: why }).unwrap();
                              flash({ type: "success", text: "Suggestion turned down." });
                            } catch (err) {
                              flash({ type: "error", text: describeApiError(err, "Could not update it.") });
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          Turn down
                        </button>
                      </>
                    </AccessGate>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ask for stock */}
      {asking_ && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6">
          <form onSubmit={submitRequest} className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Request stock</h3>
              <button type="button" onClick={() => setAsking(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              {mainStore
                ? `Goes to ${mainStore.name}. Nothing moves until they approve it.`
                : "No main store is set — a super admin sets one on the Stores screen."}
            </p>

            <div>
              <label className={labelCls}>For which store *</label>
              <select
                required
                value={reqForm.store_id}
                onChange={(e) => setReqForm((f) => ({ ...f, store_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">Select…</option>
                {myStores.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {myStores.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  You are not the custodian of any branch store.
                </p>
              )}
            </div>

            <div>
              <label className={labelCls}>Item *</label>
              <select
                required
                value={reqForm.store_item_id}
                onChange={(e) => setReqForm((f) => ({ ...f, store_item_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">Select…</option>
                {items.map((i: any) => <option key={i.id} value={i.id}>{i.name} ({i.code})</option>)}
              </select>
            </div>

            <div>
              <label className={labelCls}>How much *</label>
              <input
                type="number" required step="0.01" min="0.01"
                value={reqForm.quantity}
                onChange={(e) => setReqForm((f) => ({ ...f, quantity: e.target.value }))}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Note</label>
              <input
                value={reqForm.note}
                onChange={(e) => setReqForm((f) => ({ ...f, note: e.target.value }))}
                className={inputCls}
                placeholder="Why it is needed"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={() => setAsking(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={asking}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg disabled:opacity-40"
              >
                {asking && <Loader2 className="w-4 h-4 animate-spin" />}
                Send request
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Suggest an item */}
      {suggesting_ && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6">
          <form onSubmit={submitSuggestion} className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Suggest an item</h3>
              <button type="button" onClick={() => setSuggesting(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              For something the register does not carry. It is added once whoever keeps the
              item register accepts it — they give it its code.
            </p>

            <div>
              <label className={labelCls}>What is it *</label>
              <input
                required
                value={sugForm.name}
                onChange={(e) => setSugForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
                placeholder="e.g. A3 photocopy paper"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Category</label>
                <input
                  value={sugForm.category}
                  onChange={(e) => setSugForm((f) => ({ ...f, category: e.target.value }))}
                  className={inputCls}
                  placeholder="Stationery"
                />
              </div>
              <div>
                <label className={labelCls}>Unit</label>
                <input
                  value={sugForm.unit}
                  onChange={(e) => setSugForm((f) => ({ ...f, unit: e.target.value }))}
                  className={inputCls}
                  placeholder="reams"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Rough price</label>
                <input
                  type="number" step="0.01" min="0"
                  value={sugForm.suggested_price}
                  onChange={(e) => setSugForm((f) => ({ ...f, suggested_price: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>For which store</label>
                <select
                  value={sugForm.store_id}
                  onChange={(e) => setSugForm((f) => ({ ...f, store_id: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Not store-specific</option>
                  {myStores.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Why it is needed</label>
              <input
                value={sugForm.reason}
                onChange={(e) => setSugForm((f) => ({ ...f, reason: e.target.value }))}
                className={inputCls}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={() => setSuggesting(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={suggesting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg disabled:opacity-40"
              >
                {suggesting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send suggestion
              </button>
            </div>
          </form>
        </div>
      )}
    </StoreShell>
  );
}

export default function StoreRequestsPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <RequestsInner />
    </PageGate>
  );
}
